import { useCallback, useEffect, useState, useRef, useMemo } from "react";
import { Box, Link, Stack, Container, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Formik, Form } from "formik";
import { enqueueSnackbar } from "notistack";
import { authAPI } from "../redux/authSlice";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import {
    FormInput,
    FormCheckbox,
    Splashscreen,
    Logo,
    Footer,
} from "src/components";
import { version, utcOffset } from "src/utils/constants";
import {
    getService,
    getServiceBrandName,
    isErrorWithMessage,
} from "src/utils/helpers";
import { useAppDispatch, useAppSelector } from "src/hooks";
import type {
    Client,
    Config,
    Credentials,
    FormattedUserAndClient,
    OnDemandLogin,
    User,
} from "../types";
import { generateParaplanLoginQuery } from "../helpers";
import { loginSchema } from "../schema";
import { useTitle } from "react-use";
import { PassioService } from "src/types";
import { tripsAPI } from "src/modules/trips/redux/tripSlice";
import SSO from "../components/SSO";

/**
 * @param agency: agency name
 * @param service paraplan | navigator
 * @example /login?agency=ENGRAPHTRANSIT&service=paraplan
 */

const Login = () => {
    useTitle("Login");
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const agency = searchParams.get("agency") ?? "";

    const { service, isNavigator } = useMemo(() => {
        const service = getService();
        return {
            service,
            isNavigator: service === PassioService.navigator ? true : false,
        };
    }, []);

    const [login, { isLoading }] = authAPI.useLoginMutation();
    const [passioLogin, { isLoading: passioLoading }] =
        authAPI.usePassioLoginMutation();
    const [processing, setProcessing] = useState({
        active: false,
        message: "processing",
    });

    const {
        displayName,
        savedLogin,
        service: savedService,
    } = useAppSelector((state) => ({
        service: state.auth.service,
        savedLogin: state.auth.savedLogin,
        displayName:
            state.auth.config?.ConnectPreferences?.DisplayName ||
            state.auth.config?.AgencyName ||
            "",
    }));

    const autoLoginChecked = useRef(false); // use a ref to track if autologin has been checked, prevents login loops

    /*
     * @params resume - if user is resuming a session and has saved creds
     * @params values - user credentials could be cached or filled in form
     */
    const loadProfile = useCallback(
        async (
            auth?: OnDemandLogin,
            paraplanUser?: User,
            isNavigator = true
        ) => {
            try {
                let config = {} as Config;
                let rider = {} as Client;
                let user = paraplanUser;

                if (isNavigator) {
                    if (!auth) throw new Error();
                    // load services first so that we can format rider programs
                    setProcessing({
                        active: true,
                        message: "Loading Services",
                    });

                    const programs = await dispatch(
                        authAPI.endpoints.getPrograms.initiate({
                            id: auth.userId,
                        })
                    );

                    if (programs.isError) {
                        throw new Error("Sorry we couldn't load your services");
                    }

                    // load onDemandUser and rider profile
                    setProcessing({
                        active: true,
                        message: "Loading Account",
                    });

                    const onDemandUser = await dispatch(
                        authAPI.endpoints.getOnDemandUser.initiate(auth.id)
                    );

                    if (onDemandUser.isError) {
                        throw new Error("Sorry we couldn't load your services");
                    }

                    const profile = onDemandUser.data as FormattedUserAndClient;
                    rider = profile.client;
                    user = profile.user;
                }

                if (!user) throw new Error();
                // Only proceed if user is authorized
                if (user.PPPAccess !== 1) {
                    throw new Error("Portal access is not available");
                }

                /*
                 * Navigator config was already populated from  user object in previous step
                 * Only fetch config if service is Paraplan
                 */

                setProcessing({ active: true, message: "Loading Preferences" });

                const agencyConfig = await dispatch(
                    authAPI.endpoints.getConfig.initiate(rider.agencyId)
                );

                if (!agencyConfig.data || agencyConfig.isError) {
                    throw new Error("Sorry we couldn't load your preferences");
                }

                config = agencyConfig.data;

                /*
                 * Load passioToken, if service is Paraplan.
                 * Geofence calls are made to Passio API and need a navigator token
                 */

                // Todo: skip load when onDemandAccess can call geofence
                setProcessing({
                    active: true,
                    message: "Authorizing Passio Access",
                });

                const passioToken = await dispatch(
                    authAPI.endpoints.getPassioToken.initiate()
                );

                if (passioToken.isError) {
                    throw new Error("Sorry we couldn't authorize your access");
                }

                // load geozones
                setProcessing({ active: true, message: "Loading Geozones" });
                const agency = isNavigator ? config?.PassioUsername : "";

                const geozones = await dispatch(
                    authAPI.endpoints.getGeoZones.initiate(agency)
                );

                if (geozones.isError) {
                    throw new Error("Sorry we couldn't load your zones");
                }

                // Paraplan: Additional checks if user is a manager
                const isManager = user.ClientID === 0 || user.UserType === 1;
                if (!isNavigator && isManager) {
                    if (!user.ProviderPrograms) {
                        throw new Error("Invalid mobility manager infomation");
                    }

                    if (
                        !user.ProviderCanViewClientDetails ||
                        !user.ProviderCanRequestTrips
                    ) {
                        throw new Error("Unauthorized");
                    }
                }

                // If client is manager or service is navigator, load programs
                if (isNavigator || isManager) {
                    const type = isNavigator ? "Services" : "Programs";
                    setProcessing({ active: true, message: `Loading ${type}` });

                    const programs = await dispatch(
                        authAPI.endpoints.getPrograms.initiate({
                            id: rider.agencyId,
                        })
                    );

                    if (programs.isError) {
                        const message = isNavigator
                            ? "Sorry we couldn't find any services for your agency"
                            : "Sorry we couldn't load your programs";
                        throw new Error(message);
                    }
                } else {
                    // Paraplan: Load client profile
                    setProcessing({
                        active: true,
                        message: "Loading Client Profile",
                    });

                    const client = await dispatch(
                        authAPI.endpoints.getClient.initiate(user.ClientID)
                    );

                    if (client.isError) {
                        throw new Error("Sorry we couldn't load your profile");
                    }
                }

                // check location preferences
                const preferences = config?.ConnectPreferences;
                if (
                    !preferences?.CanRequestAnyPULocation ||
                    !preferences?.CanRequestAnyDOLocation
                ) {
                    // load places
                    setProcessing({ active: true, message: "Loading Places" });

                    const places = await dispatch(
                        authAPI.endpoints.getPlaces.initiate()
                    );

                    if (places.isError) {
                        throw new Error("Sorry we couldn't load places");
                    }
                }

                if (isNavigator) {
                    // load devices
                    setProcessing({ active: true, message: "Loading Devices" });
                    const devices = await dispatch(
                        tripsAPI.endpoints.getDevices.initiate(rider.agencyId)
                    );

                    if (devices.isError) {
                        throw new Error("Sorry we couldn't load devices");
                    }

                    // load MDTs
                    setProcessing({ active: true, message: "Loading MDTs" });
                    const MDTs = await dispatch(
                        tripsAPI.endpoints.getMDTs.initiate()
                    );

                    if (MDTs.isError) {
                        throw new Error("Sorry we couldn't load MDTs");
                    }
                }
                /*
                 * All state is filtered directly in redux state matchers after request complete
                 * check the api slices for each module to learn more
                 */

                enqueueSnackbar("Login successful", { variant: "success" });

                // if user was redirected to login page, redirect back to where they came from
                if (location.state?.from) {
                    return navigate(location.state.from, {
                        replace: true,
                        state: {},
                    });
                } else if (user.UserType === 1) {
                    // Redirect to riders list if user is a mobility manager
                    return navigate("/riders");
                } else {
                    return navigate("/trips");
                }
            } catch (error) {
                const message = isErrorWithMessage(error)
                    ? error.message
                    : "Login failed. Please check your password/PIN";
                enqueueSnackbar(message, { variant: "error" });
            }

            setProcessing({ active: false, message: "" });
        },
        [dispatch, location, navigate]
    );

    const handleLogin = useCallback(
        async (values: Credentials, resume = false) => {
            try {
                // Try login to each service, switch if it fails, try again, then exit if both fail
                let parsedService = resume ? savedService : service;
                for (let i = 0; i < 2; i++) {
                    try {
                        if (parsedService === PassioService.navigator) {
                            const auth = await passioLogin(values).unwrap();
                            await loadProfile(auth, undefined, true);
                            return;
                        } else {
                            // when resuming, the credentials were already hashed before saving
                            const request = resume
                                ? values
                                : {
                                      ...generateParaplanLoginQuery(values),
                                      autoLogin: values.autoLogin,
                                  };

                            const user = await login(request).unwrap();
                            await loadProfile(undefined, user, false);
                            return;
                        }
                    } catch (error) {
                        if (i >= 1) {
                            enqueueSnackbar(
                                "Login failed. Please check your password/PIN",
                                { variant: "error" }
                            );
                            break; // Exit if both services fail
                        }
                        // Switch service and try again
                        parsedService =
                            service === PassioService.navigator
                                ? PassioService.paraplan
                                : PassioService.navigator;
                    }
                }
            } catch (error) {
                enqueueSnackbar(
                    "Login failed. Please check your password/PIN",
                    { variant: "error" }
                );
            }
        },
        [loadProfile, login, passioLogin, savedService, service]
    );

    // handle autologin
    useEffect(() => {
        if (autoLoginChecked.current) return;
        if (!(savedLogin && savedLogin?.email && savedLogin?.password)) {
            autoLoginChecked.current = true;
            return;
        }
        setProcessing({
            active: true,
            message: "Auto login enabled, we are restoring your session",
        });
        autoLoginChecked.current = true;
        handleLogin(savedLogin, true);
    }, [savedLogin, handleLogin]);

    // show loader when processing
    if (processing.active) {
        return <Splashscreen description={processing.message} />;
    }

    return (
        <Container
            component="main"
            maxWidth="xs"
            sx={{
                display: { md: "grid" },
                placeItems: { md: "center" },
                height: { md: "100vh" },
            }}
        >
            <Logo sx={{ mt: { xs: 2, md: 8 } }} />

            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                }}
            >
                {displayName && (
                    <Typography component="h2" variant="h5" my={2}>
                        {displayName}
                    </Typography>
                )}

                {isNavigator && <SSO />}

                <Formik
                    initialValues={{
                        email: "",
                        password: "",
                        autoLogin: isNavigator ? true : false,
                    }}
                    validationSchema={loginSchema}
                    onSubmit={(values) => handleLogin(values)}
                >
                    <Box component={Form} sx={{ mt: 1 }}>
                        <FormInput
                            fullWidth
                            name="email"
                            label="Email address"
                            margin="normal"
                            inputProps={{
                                autoComplete: "email",
                            }}
                        />
                        <FormInput
                            fullWidth
                            name="password"
                            label="Password or Pin"
                            type="password"
                            margin="normal"
                            inputProps={{
                                autoComplete: "password",
                            }}
                        />

                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                        >
                            <FormCheckbox
                                label="Remember me"
                                name="autoLogin"
                            />
                            {isNavigator && (
                                <Link
                                    href={`/recovery?service=${PassioService.navigator}`}
                                >
                                    Forgot password?
                                </Link>
                            )}
                        </Stack>
                        <LoadingButton
                            size="large"
                            type="submit"
                            fullWidth
                            variant="contained"
                            loading={isLoading || passioLoading}
                            sx={{ my: 2, py: 1, color: "primary.contrastText" }}
                        >
                            {isLoading || passioLoading
                                ? "Verifying account"
                                : "Sign In"}
                        </LoadingButton>
                    </Box>
                </Formik>

                {/* Signup is only available via agency */}
                {agency && displayName && (
                    <Typography align="center" my={2}>
                        Need a connect account?{" "}
                        <Link href="/signup">Sign up</Link>
                    </Typography>
                )}

                <Typography align="center" variant="body2">
                    {`${getServiceBrandName(
                        service
                    )} ${version} UTC ${utcOffset}`}
                </Typography>
            </Box>

            <Footer />
        </Container>
    );
};

export default Login;
